import { acceptLanguage } from '@/shared/i18n/instance';
import { networkError, toApiError } from '@/shared/services/api/api-error';
import { tokenStorage } from '@/shared/services/api/token-storage';
import type { StoredTokens } from '@/shared/services/api/token-storage';
import { elevation } from '@/shared/services/auth/elevation';
import { reportServerReachable } from '@/shared/services/network/connectivity';

import { ENV } from '@/config/env.config';

/**
 * Client HTTP de l'application.
 *
 * Trois responsabilités, et rien d'autre : porter le jeton, **rafraichir une
 * seule fois** quand il expire, et transformer toute réponse en échec en une
 * `ApiError` affichable. Les écrans ne voient jamais un code HTTP.
 */
const BASE_URL = `${ENV.API_URL.replace(/\/$/, '')}/api/v1`;
const TIMEOUT_MS = 20_000;

/** En-tête qui porte la preuve de saisie du code de confidentialité. */
const ELEVATION_HEADER = 'x-bul-elevation';
/** Refus du serveur : le geste touche à l'argent, et la preuve manque ou a expiré. */
const PIN_REQUIRED = 'PIN_REQUIRED';

type Query = Record<string, string | number | boolean | undefined | null>;

interface RequestOptions {
  query?: Query;
  body?: unknown;
  /** Route publique : on n'attache pas de jeton et on ne tente pas de rafraichir. */
  anonymous?: boolean;
  /**
   * Requete de clôture (`/auth/logout`). Elle porte le jeton : c'est la session
   * qu'elle ferme, mais un 401 y est le résultat attendu, pas un signal de
   * session perdue.
   *
   * Sans cette distinction, fermer une session déja morte partait en boucle :
   * 401 → rotation refusée → « session perdue » → déconnexion → nouvel appel a
   * `/auth/logout` → 401… sans fin, une requete réseau par tour.
   */
  endsSession?: boolean;
  signal?: AbortSignal;
  /** Délai propre à la requete : un dépot de photo est plus long qu'un GET. */
  timeoutMs?: number;
  /**
   * La requête survit à la fermeture de la page (`fetch` avec `keepalive`) : pour
   * remettre les derniers signaux du fil au départ de l'onglet. Corps limité à
   * 64 Ko par le navigateur.
   */
  keepalive?: boolean;
}

/**
 * Issue d'une tentative de rotation.
 *
 * `refused` et `unreachable` ne se confondent pas : le serveur qui refuse dit
 * que la session est morte, le réseau qui tombe ne dit rien du tout. Les
 * mélanger déconnectait l'utilisateur au premier tunnel.
 */
type RefreshOutcome =
  { kind: 'renewed'; tokens: StoredTokens } | { kind: 'refused' } | { kind: 'unreachable' };

let tokens: StoredTokens | null = null;
/**
 * Rafraichissement en cours. Quand plusieurs requetes tombent en 401 en meme
 * temps (un écran en charge trois listes), elles attendent toutes la meme
 * promesse : le jeton ne tourne qu'une fois, et les autres ne sont pas
 * invalidées par une rotation concurrente.
 */
let refreshing: Promise<RefreshOutcome> | null = null;
let onSessionLost: (() => void) | null = null;

export const httpClient = {
  /**
   * Racine de l'API. Utile aux rares adresses qui ne sont pas des requetes
   * JSON : la connexion Google s'ouvre dans le navigateur du système, pas ici.
   */
  baseUrl: BASE_URL,

  /** Restaure la session au démarrage. */
  async restore(): Promise<StoredTokens | null> {
    tokens = await tokenStorage.load();
    return tokens;
  },

  async setTokens(next: StoredTokens): Promise<void> {
    tokens = next;
    await tokenStorage.save(next);
  },

  async clearTokens(): Promise<void> {
    tokens = null;
    // La preuve de saisie du code appartient à la session qui se ferme.
    elevation.clear();
    await tokenStorage.clear();
  },

  getAccessToken(): string | null {
    return tokens?.accessToken ?? null;
  },

  /**
   * Force une rotation du jeton d'acces et rend le nouveau.
   *
   * Le socket en a besoin : il n'a pas de 401 pour le conduire a la rotation,
   * seulement une poignée de main refusée. Sans cela, une application restée en
   * arriere-plan plus d'un quart d'heure repartait indéfiniment avec un jeton
   * périmé, et le temps réel ne revenait jamais. On passe par le meme verrou
   * que les requetes HTTP : deux rotations concurrentes s'invalideraient.
   */
  async renewAccessToken(): Promise<string | null> {
    const outcome = await refreshTokens();
    if (outcome.kind === 'renewed') {
      return outcome.tokens.accessToken;
    }
    if (outcome.kind === 'refused') {
      onSessionLost?.();
    }
    return null;
  },

  /** Prévenu quand la session est définitivement perdue (rafraichissement refusé). */
  onSessionLost(handler: () => void): void {
    onSessionLost = handler;
  },

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('GET', path, options);
  },
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('POST', path, { ...options, body });
  },
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PUT', path, { ...options, body });
  },
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PATCH', path, { ...options, body });
  },
  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('DELETE', path, options);
  },
};

async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
  isRetry = false,
  afterChallenge = false,
): Promise<T> {
  const url = BASE_URL + path + buildQuery(options.query);

  // Un envoi multipart porte sa propre frontière : fixer nous-mêmes le
  // Content-Type la ferait disparaître et le serveur ne saurait plus découper.
  const isMultipart = options.body instanceof FormData;

  // Chaque appel porte la langue affichée : c'est la façon standard de la dire,
  // et le jour ou l'API traduira ses refus ou ses e-mails, il n'y aura rien a
  // changer ici. En attendant, c'est `api-error` qui traduit ce qu'il peut.
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': acceptLanguage(),
  };
  if (options.body !== undefined && !isMultipart) {
    headers['Content-Type'] = 'application/json';
  }
  if (!options.anonymous && tokens?.accessToken) {
    headers.Authorization = `Bearer ${tokens.accessToken}`;
    // La preuve part avec chaque requête tant qu'elle vaut : le serveur ne la
    // lit que sur les gestes qui touchent à l'argent.
    const proof = elevation.token();
    if (proof) {
      headers[ELEVATION_HEADER] = proof;
    }
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? TIMEOUT_MS);
  options.signal?.addEventListener('abort', () => controller.abort());

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body:
        options.body === undefined
          ? undefined
          : isMultipart
            ? (options.body as FormData)
            : JSON.stringify(options.body),
      signal: controller.signal,
      keepalive: options.keepalive,
    });
  } catch (error) {
    throw networkError(error);
  } finally {
    clearTimeout(timeout);
  }

  // Une réponse recue, meme en échec, prouve que le serveur est la : les
  // requetes mises en pause par une coupure peuvent repartir. L'inverse n'est
  // pas vrai : un seul envoi perdu ne conclut pas « hors ligne », c'est le
  // canal temps réel qui en juge, lui qui réessaie en continu.
  reportServerReachable(true);

  // Jeton périmé : on tente une rotation, puis on rejoue la requete une fois.
  if (response.status === 401 && !options.anonymous && !options.endsSession && !isRetry) {
    const outcome = await refreshTokens();
    if (outcome.kind === 'renewed') {
      return request<T>(method, path, options, true, afterChallenge);
    }
    // Session perdue seulement si le serveur l'a dit. Une coupure pendant la
    // rotation laisse les jetons en place : l'utilisateur retentera, il n'est
    // pas renvoyé a l'écran de connexion pour un métro traversé.
    if (outcome.kind === 'refused') {
      onSessionLost?.();
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await readBody(response);

  // Le geste touche à l'argent et la preuve manque, ou a expiré : on demande le
  // code, puis on rejoue la requête **une fois**. C'est ce qui évite à chaque
  // écran de savoir quels gestes sont protégés : le serveur le dit, ici.
  if (
    response.status === 403 &&
    !afterChallenge &&
    (payload as { error?: string } | null)?.error === PIN_REQUIRED
  ) {
    elevation.clear();
    if (await elevation.challenge()) {
      return request<T>(method, path, options, isRetry, true);
    }
  }

  if (!response.ok) {
    throw toApiError(response.status, payload);
  }
  return payload as T;
}

async function refreshTokens(): Promise<RefreshOutcome> {
  if (!tokens?.refreshToken) {
    return { kind: 'refused' };
  }
  refreshing ??= rotate(tokens.refreshToken).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function rotate(refreshToken: string): Promise<RefreshOutcome> {
  try {
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': acceptLanguage(),
      },
      body: JSON.stringify({ refreshToken }),
    });
    reportServerReachable(true);
    if (!response.ok) {
      await httpClient.clearTokens();
      return { kind: 'refused' };
    }
    const body = (await response.json()) as StoredTokens;
    const next = { accessToken: body.accessToken, refreshToken: body.refreshToken };
    await httpClient.setTokens(next);
    return { kind: 'renewed', tokens: next };
  } catch {
    // Panne réseau pendant la rotation : on garde les jetons, l'utilisateur
    // retentera. Les effacer déconnecterait pour une simple coupure : et
    // l'appelant ne doit pas non plus conclure a une session perdue.
    return { kind: 'unreachable' };
  }
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function buildQuery(query?: Query): string {
  if (!query) {
    return '';
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}
